import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
  Button,
  Collapse
} from "reactstrap"
import { formatDateForAPI, getDefaultDateRange, getEmptyDateRange } from "../../../../helpers/rfq_dashboard_api"
import { get } from "../../../../helpers/api_helper"
import * as url from "../../../../helpers/url_helper"

const FilterPanel = ({ filters, onFilterChange, loading }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [projects, setProjects] = useState([])
  const [vendors, setVendors] = useState([])
  // const [statuses, setStatuses] = useState([])
  const [serviceHeads, setServiceHeads] = useState([])
  const [serviceSubheads, setServiceSubheads] = useState([])
  const [loadingOptions, setLoadingOptions] = useState(false)

  // Local filter state
  const [localFilters, setLocalFilters] = useState({
    ...filters,
    serviceHeadId: filters.serviceHeadId || '',
    serviceSubheadId: filters.serviceSubheadId || ''
  })

  // Load filter options
  useEffect(() => {
    loadFilterOptions()
  }, [])

  const loadFilterOptions = async () => {
    setLoadingOptions(true)
    try {
      // Load projects from RFQ Dashboard endpoint only
      try {
        console.log("Loading RFQ projects from dashboard endpoint:", url.RFQ_DASHBOARD_PROJECTS_FILTER)
        const projectsResponse = await get(url.RFQ_DASHBOARD_PROJECTS_FILTER)
        console.log("RFQ Projects response:", projectsResponse)

        if (projectsResponse && projectsResponse.status) {
          setProjects(projectsResponse.data || [])
          console.log("RFQ Projects loaded successfully:", projectsResponse.data?.length || 0)
        } else {
          throw new Error("Failed to load RFQ projects from dashboard")
        }
      } catch (projectError) {
        console.warn("Failed to load RFQ projects from dashboard, using mock data:", projectError)
        setProjects([
          { id: 1, project_name: "Corporate Tower" },
          { id: 2, project_name: "Mall Complex" },
          { id: 3, project_name: "Residential Block A" },
          { id: 4, project_name: "Office Building B" }
        ])
      }

      try {
        // Load active vendors from RFQ Dashboard endpoint
        console.log("Loading active vendors from:", url.RFQ_DASHBOARD_VENDORS_FILTER)
        const vendorsResponse = await get(url.RFQ_DASHBOARD_VENDORS_FILTER)
        console.log("Active vendors response:", vendorsResponse)

        if (vendorsResponse && vendorsResponse.status) {
          setVendors(vendorsResponse.data || [])
          console.log("Active vendors loaded successfully:", vendorsResponse.data?.length || 0)
        } else {
          throw new Error("Failed to load active vendors")
        }
      } catch (vendorError) {
        console.warn("Failed to load active vendors, using mock data:", vendorError)
        setVendors([
          { id: 1, vendor_name: "ABC Construction" },
          { id: 2, vendor_name: "XYZ Services" },
          { id: 3, vendor_name: "PQR Contractors" },
          { id: 4, vendor_name: "LMN Solutions" }
        ])
      }

      try {
        // Load service heads
        console.log("Loading service heads from:", url.RFQ_DASHBOARD_SERVICE_HEADS_FILTER)
        const serviceHeadsResponse = await get(url.RFQ_DASHBOARD_SERVICE_HEADS_FILTER)
        console.log("Service heads response:", serviceHeadsResponse)

        if (serviceHeadsResponse && serviceHeadsResponse.status) {
          setServiceHeads(serviceHeadsResponse.data || [])
          console.log("Service heads loaded successfully:", serviceHeadsResponse.data?.length || 0)
        } else {
          throw new Error("Failed to load service heads")
        }
      } catch (serviceHeadError) {
        console.warn("Failed to load service heads, using mock data:", serviceHeadError)
        setServiceHeads([
          { id: 1, head_text: "Construction Services" },
          { id: 2, head_text: "IT Services" },
          { id: 3, head_text: "Consulting Services" },
          { id: 4, head_text: "Maintenance Services" }
        ])
      }

      try {
        // Load service subheads
        console.log("Loading service subheads from:", url.RFQ_DASHBOARD_SERVICE_SUBHEADS_FILTER)
        const serviceSubheadsResponse = await get(url.RFQ_DASHBOARD_SERVICE_SUBHEADS_FILTER)
        console.log("Service subheads response:", serviceSubheadsResponse)

        if (serviceSubheadsResponse && serviceSubheadsResponse.status) {
          setServiceSubheads(serviceSubheadsResponse.data || [])
          console.log("Service subheads loaded successfully:", serviceSubheadsResponse.data?.length || 0)
        } else {
          throw new Error("Failed to load service subheads")
        }
      } catch (serviceSubheadError) {
        console.warn("Failed to load service subheads, using mock data:", serviceSubheadError)
        setServiceSubheads([
          { id: 1, subhead_text: "Civil Construction", head_id: 1 },
          { id: 2, subhead_text: "Electrical Work", head_id: 1 },
          { id: 3, subhead_text: "Software Development", head_id: 2 },
          { id: 4, subhead_text: "Network Setup", head_id: 2 }
        ])
      }

      // Load RFQ statuses (these are standard, so we can use static data)
      // setStatuses([
      //   { id: 1, status: "Created" },
      //   { id: 2, status: "In Progress" },
      //   { id: 3, status: "Completed" },
      //   { id: 4, status: "On Hold" },
      //   { id: 5, status: "Cancelled" }
      // ])

    } catch (error) {
      console.error("Error loading filter options:", error)
      // Fallback to mock data on complete failure
      setProjects([
        { id: 1, project_name: "Sample Project 1" },
        { id: 2, project_name: "Sample Project 2" }
      ])
      setVendors([
        { id: 1, vendor_name: "Sample Vendor 1" },
        { id: 2, vendor_name: "Sample Vendor 2" }
      ])
      // setStatuses([
      //   { id: 1, status: "Created" },
      //   { id: 2, status: "In Progress" },
      //   { id: 3, status: "Completed" }
      // ])
    } finally {
      setLoadingOptions(false)
    }
  }

  // Handle local filter changes
  const handleLocalFilterChange = (key, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [key]: value
    }))
  }

  // Handle service head change and reload subheads
  const handleServiceHeadChange = async (value) => {
    handleLocalFilterChange('serviceHeadId', value)

    // Reset subhead when head changes
    handleLocalFilterChange('serviceSubheadId', '')

    // Load subheads for the selected head
    if (value) {
      try {
        console.log("Loading service subheads for head:", value)
        const serviceSubheadsResponse = await get(`${url.RFQ_DASHBOARD_SERVICE_SUBHEADS_FILTER}?head_id=${value}`)
        console.log("Filtered service subheads response:", serviceSubheadsResponse)

        if (serviceSubheadsResponse && serviceSubheadsResponse.status) {
          setServiceSubheads(serviceSubheadsResponse.data || [])
          console.log("Filtered service subheads loaded successfully:", serviceSubheadsResponse.data?.length || 0)
        }
      } catch (error) {
        console.warn("Failed to load filtered service subheads:", error)
      }
    } else {
      // If no head selected, load all subheads
      try {
        const serviceSubheadsResponse = await get(url.RFQ_DASHBOARD_SERVICE_SUBHEADS_FILTER)
        if (serviceSubheadsResponse && serviceSubheadsResponse.status) {
          setServiceSubheads(serviceSubheadsResponse.data || [])
        }
      } catch (error) {
        console.warn("Failed to reload all service subheads:", error)
      }
    }
  }

  // Apply filters
  const applyFilters = () => {
    console.log("Applying filters:", localFilters)
    onFilterChange(localFilters)
  }

  // Reset filters
  const resetFilters = () => {
    const defaultFilters = {
      ...getEmptyDateRange(),
      project_id: null,
      vendor_id: null,
      // status_id: null,
      serviceHeadId: null,
      serviceSubheadId: null
    }
    setLocalFilters(defaultFilters)
    onFilterChange(defaultFilters)

    // Also reload all subheads when reset
    loadFilterOptions()
  }

  // Quick date range presets
  const setQuickDateRange = (range) => {
    const endDate = new Date()
    const startDate = new Date()

    switch (range) {
      case "today":
        startDate.setHours(0, 0, 0, 0)
        endDate.setHours(23, 59, 59, 999)
        break
      case "week":
        startDate.setDate(startDate.getDate() - 7)
        break
      case "month":
        startDate.setMonth(startDate.getMonth() - 1)
        break
      case "quarter":
        startDate.setMonth(startDate.getMonth() - 3)
        break
      case "year":
        startDate.setFullYear(startDate.getFullYear() - 1)
        break
      default:
        return
    }

    const newFilters = {
      ...localFilters,
      start_date: formatDateForAPI(startDate),
      end_date: formatDateForAPI(endDate)
    }
    console.log("Setting quick date range:", range, newFilters)
    setLocalFilters(newFilters)
    onFilterChange(newFilters)
  }

  return (
    <Card className="filter-panel">
      <CardBody>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="mb-0">
            <i className="bx bx-filter-alt me-2"></i>
            Filters
          </h6>
          <Button
            color="link"
            size="md"
            onClick={() => setIsOpen(!isOpen)}
            className="filter-toggle-btn p-2"
            style={{
              minWidth: '40px',
              minHeight: '40px',
              borderRadius: '6px',
              transition: 'all 0.3s ease'
            }}
          >
            <i
              className={`bx bx-chevron-${isOpen ? 'up' : 'down'}`}
              style={{ fontSize: '1.5rem', fontWeight: 'bold' }}
            ></i>
          </Button>
        </div>

        <Collapse isOpen={isOpen}>
          <Row>
            {/* Date Range */}
            <Col md={3}>
              <FormGroup>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={localFilters.start_date || ""}
                  onChange={(e) => handleLocalFilterChange("start_date", e.target.value)}
                  disabled={loading}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={localFilters.end_date || ""}
                  onChange={(e) => handleLocalFilterChange("end_date", e.target.value)}
                  disabled={loading}
                />
              </FormGroup>
            </Col>

            {/* Project Filter */}
            <Col md={2}>
              <FormGroup>
                <Label>Project</Label>
                <Input
                  type="select"
                  value={localFilters.project_id || ""}
                  onChange={(e) => handleLocalFilterChange("project_id", e.target.value || null)}
                  disabled={loading || loadingOptions}
                >
                  <option key="all-projects" value="">All Projects</option>
                  {projects.map((project, index) => (
                    <option key={`project-${project.id || index}`} value={project.id}>
                      {project.project_name || `Project ${project.id}`}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>

            {/* Vendor Filter */}
            <Col md={2}>
              <FormGroup>
                <Label>Vendor</Label>
                <Input
                  type="select"
                  value={localFilters.vendor_id || ""}
                  onChange={(e) => handleLocalFilterChange("vendor_id", e.target.value || null)}
                  disabled={loading || loadingOptions}
                >
                  <option key="all-vendors" value="">All Vendors</option>
                  {vendors.map((vendor, index) => (
                    <option key={`vendor-${vendor.id || index}`} value={vendor.id}>
                      {vendor.vendor_name || `Vendor ${vendor.id}`}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>

            {/* Status Filter */}
            {/* <Col md={2}>
              <FormGroup>
                <Label>Status</Label>
                <Input
                  type="select"
                  value={localFilters.status_id || ""}
                  onChange={(e) => handleLocalFilterChange("status_id", e.target.value || null)}
                  disabled={loading}
                >
                  <option key="all-statuses" value="">All Statuses</option>
                  {statuses.map((status, index) => (
                    <option key={`status-${status.id || index}`} value={status.id}>
                      {status.status}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col> */}
          </Row>

          {/* Second Row - Service Head and Subhead Filters */}
          <Row className="mb-3">
            {/* Service Head Filter */}
            <Col md={3}>
              <FormGroup>
                <Label>Good & Service Head</Label>
                <Input
                  type="select"
                  value={localFilters.serviceHeadId || ""}
                  onChange={(e) => handleServiceHeadChange(e.target.value || null)}
                  disabled={loading || loadingOptions}
                >
                  <option key="all-service-heads" value="">All Service Heads</option>
                  {serviceHeads.map((head, index) => (
                    <option key={`service-head-${head.id || index}`} value={head.id}>
                      {head.head_text || `Service Head ${head.id}`}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>

            {/* Service Subhead Filter */}
            <Col md={3}>
              <FormGroup>
                <Label>Good & Service Subhead</Label>
                <Input
                  type="select"
                  value={localFilters.serviceSubheadId || ""}
                  onChange={(e) => handleLocalFilterChange("serviceSubheadId", e.target.value || null)}
                  disabled={loading || loadingOptions}
                >
                  <option key="all-service-subheads" value="">All Service Subheads</option>
                  {serviceSubheads
                    .filter(subhead => !localFilters.serviceHeadId || subhead.head_id == localFilters.serviceHeadId)
                    .map((subhead, index) => (
                      <option key={`service-subhead-${subhead.id || index}`} value={subhead.id}>
                        {subhead.subhead_text || `Service Subhead ${subhead.id}`}
                      </option>
                    ))}
                </Input>
              </FormGroup>
            </Col>

            <Col>
              <Label className="form-label">Quick Date Ranges:</Label>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  size="sm"
                  color="outline-primary"
                  onClick={() => setQuickDateRange("today")}
                  disabled={loading}
                >
                  Today
                </Button>
                <Button
                  size="sm"
                  color="outline-primary"
                  onClick={() => setQuickDateRange("week")}
                  disabled={loading}
                >
                  Last 7 Days
                </Button>
                <Button
                  size="sm"
                  color="outline-primary"
                  onClick={() => setQuickDateRange("month")}
                  disabled={loading}
                >
                  Last Month
                </Button>
                <Button
                  size="sm"
                  color="outline-primary"
                  onClick={() => setQuickDateRange("quarter")}
                  disabled={loading}
                >
                  Last Quarter
                </Button>
                <Button
                  size="sm"
                  color="outline-primary"
                  onClick={() => setQuickDateRange("year")}
                  disabled={loading}
                >
                  Last Year
                </Button>
              </div>
            </Col>
          </Row>

          {/* Action Buttons */}
          <Row>
            <Col>
              <div className="d-flex gap-2">
                <Button
                  color="primary"
                  size="sm"
                  onClick={applyFilters}
                  disabled={loading}
                  className="px-3 py-1"
                  style={{ fontSize: '0.875rem', fontWeight: '500' }}
                >
                  <i className="bx bx-check me-1"></i>
                  Apply Filters
                </Button>
                <Button
                  color="secondary"
                  size="sm"
                  onClick={resetFilters}
                  disabled={loading}
                  className="px-2 py-1"
                >
                  <i className="bx bx-reset me-1"></i>
                  Reset
                </Button>
              </div>
            </Col>
          </Row>
        </Collapse>

        {/* Active Filters Display */}
        {!isOpen && (
          <div className="active-filters">
            <small className="text-muted">
              Active Filters: 
              {localFilters.start_date && (
                <span className="badge badge-soft-primary ms-1">
                  {localFilters.start_date} to {localFilters.end_date}
                </span>
              )}
              {localFilters.project_id && (
                <span className="badge badge-soft-info ms-1">
                  Project: {projects.find(p => p.id == localFilters.project_id)?.project_name}
                </span>
              )}
              {localFilters.vendor_id && (
                <span className="badge badge-soft-success ms-1">
                  Vendor: {vendors.find(v => v.id == localFilters.vendor_id)?.vendor_name}
                </span>
              )}
              {/* {localFilters.status_id && (
                <span className="badge badge-soft-warning ms-1">
                  Status: {statuses.find(s => s.id == localFilters.status_id)?.status}
                </span>
              )} */}
              {localFilters.serviceHeadId && (
                <span className="badge badge-soft-secondary ms-1">
                  Service Head: {serviceHeads.find(h => h.id == localFilters.serviceHeadId)?.head_text}
                </span>
              )}
              {localFilters.serviceSubheadId && (
                <span className="badge badge-soft-dark ms-1">
                  Service Subhead: {serviceSubheads.find(s => s.id == localFilters.serviceSubheadId)?.subhead_text}
                </span>
              )}
              {!localFilters.start_date && !localFilters.project_id && !localFilters.vendor_id && /* !localFilters.status_id && */ !localFilters.serviceHeadId && !localFilters.serviceSubheadId && (
                <span className="text-muted ms-1">None</span>
              )}
            </small>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

export default FilterPanel
